import { ApolloClient, FetchPolicy, InMemoryCache } from '@apollo/client';
import _ from 'lodash';
import { isoTimeStampUtc } from './dateUtils';

interface IFetchOptions {
  fetchPolicy?: FetchPolicy;
  variables?: {
    after?: string;
    first?: number;
    filter?: any;
    sort?: any[];
  };
}

interface IFetchOneOptions {
  fetchPolicy?: FetchPolicy;
  variables?: {
    filter?: any;
  };
}

class API {
  public client: ApolloClient<InMemoryCache | object>;
  public defaultFetchPolicy: FetchPolicy = 'cache-first';

  constructor(client: ApolloClient<InMemoryCache | object>) {
    this.client = client;
  }

  // Fetch a single row by id
  public async fetchOne<T>(query: any, id: string, resultKey: string, options?: IFetchOneOptions): Promise<T> {
    const result = await this.client.query({
      fetchPolicy: _.get(options, 'fetchPolicy', this.defaultFetchPolicy),
      query,
      variables: this.buildFetchOneVariables(id, options),
    });

    const item = _.get(result, `data[${resultKey}][0]`, null);
    if (item !== undefined) {
      return item;
    } else {
      throw new SyntaxError('Could not retrieve item with result key');
    }
  }

  // Fetch a page of data
  public async fetch<T>(query: any, resultKey: string, options?: IFetchOptions): Promise<T[]> {
    const result = await this.client.query({
      fetchPolicy: _.get(options, 'fetchPolicy', 'cache-first'),
      query,
      variables: _.get(options, 'variables', {}),
    });

    const items = _.get(result, `data[${resultKey}]`, []);

    if (items !== undefined) {
      return items;
    } else {
      throw new SyntaxError('Could not retrieve items with result key');
    }
  }

  // Update an array of items
  public async update<T>(
    mutation: any,
    items: T[],
    formData: any,
    variableKey: string,
    resultKey: string,
    itemCallback?: (result: T, cache?: any) => void
  ) {
    const updatedItems: T[] = [];
    const promises = _.map([...items], async (itemToUpdate: { id: string }) => {
      const variables = { [variableKey]: { ...formData, id: itemToUpdate.id } };

      return this.client.mutate({
        mutation,
        update: (cache, results: any) => {
          const updatedItem = results.data[resultKey];

          if (updatedItem !== undefined) {
            if (itemCallback) {
              itemCallback(updatedItem, cache);
            }
            updatedItems.push(updatedItem);
          } else {
            throw SyntaxError('Bad result key');
          }
        },
        variables,
      });
    });

    await Promise.all(promises);
    return updatedItems;
  }

  // Creates a single item
  public async createOne<T>(mutation: any, item: T, variableKey: string, resultKey: string) {
    const result = await this.client.mutate({
      mutation,
      variables: {
        [variableKey]: { ...item, createdOn: isoTimeStampUtc() },
      },
    });

    const createdItem = result.data[resultKey];

    if (createdItem) {
      return createdItem;
    } else {
      throw SyntaxError('API: Could not retrieve data with resultKey');
    }
  }

  public async create<T>(
    mutation: any,
    items: T[],
    variableKey: string,
    resultKey: string,
    itemCallback?: (result: T, cache?: any) => void
  ) {
    const createdItems: T[] = [];
    const promises = _.map([...items], async (itemToCreate: T) => {
      return this.client.mutate({
        mutation,
        update: (cache, results: any) => {
          const createdItem = results.data[resultKey];

          if (createdItem !== undefined) {
            if (itemCallback) {
              itemCallback(createdItem, cache);
            }
            createdItems.push(createdItem);
          } else {
            throw SyntaxError('Bad result key');
          }
        },
        variables: {
          [variableKey]: { ...itemToCreate, createdOn: isoTimeStampUtc() },
        },
      });
    });

    await Promise.all(promises);
    return createdItems;
  }

  // Creates the variables property for the fetchOne query
  public buildFetchOneVariables(id: string, options?: IFetchOneOptions) {
    const filter = _.get(options, 'variables.filter', null);
    return { filter: filter ? { id: { equal: id }, ...filter } : { id: { equal: id } } };
  }
}

export default API;
